import { onUnmounted, type Ref } from "vue";

type InitialFocusOpt =
  | string
  | HTMLElement
  | null
  | (() => HTMLElement | null | undefined);

export function useFocusTrap(
  rootRef: Ref<HTMLElement | null>,
  opts?: {
      includeRoot?: boolean;      // initial focus
      initialFocus?: InitialFocusOpt;
      onEscape?: () => void;
      returnFocus?: boolean;
  }
) {
    const includeRoot = opts?.includeRoot ?? true;
    const returnFocus = opts?.returnFocus ?? true;

    let enabled = false;
    let prevActive: HTMLElement | null = null;

    const focusableSelector = [
        'a[href]',
        'area[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'iframe',
        'audio[controls]',
        'video[controls]',
        '[contenteditable="true"]',
        '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    function isVisible(el: HTMLElement) {
        const style = getComputedStyle(el);
        return style.visibility !== 'hidden' && style.display !== 'none';
    }

    function getFocusable(root: HTMLElement): HTMLElement[] {
        return Array
          .from(root.querySelectorAll<HTMLElement>(focusableSelector))
          .filter(el => !el.hasAttribute('disabled') && isVisible(el));
    }

    function resolveInitial(root: HTMLElement): HTMLElement | null {
        const opt = opts?.initialFocus;
        if (!opt) { return null; }

        if (typeof opt === 'function') { return opt() ?? null; }
        if (typeof opt === 'string') {
            return (root.querySelector(opt) as HTMLElement) ?? null;
        }
        return opt;
    }

    function focusSafely(el: HTMLElement | null) {
        el?.focus({ preventScroll: true });
    }

    // Focus cycle - within root
    function getCycle(root: HTMLElement): HTMLElement[] {
        return getFocusable(root);
    }

    function onKeyDown(e: KeyboardEvent) {
        if (!enabled) { return; }

        const root = rootRef.value;
        if (!root) { return; }

        if (e.key === 'Escape' && opts?.onEscape) {
            e.stopPropagation();
            e.preventDefault();
            opts.onEscape();
            return;
        }

        if (e.key !== 'Tab') { return; }

        const cycle = getCycle(root);
        if (cycle.length === 0) {
            return;
        }

        const active = (document.activeElement as HTMLElement) ?? root;
        let i = cycle.indexOf(active);

        e.preventDefault();

        if (i === -1) {
            focusSafely(e.shiftKey ? cycle[cycle.length - 1] : cycle[0]);
            return;
        }

        i = (i + (e.shiftKey ? -1 : 1) + cycle.length) % cycle.length;
        focusSafely(cycle[i]);
    }

    function onFocusIn(e: FocusEvent) {
        if (!enabled) { return; }

        const root = rootRef.value;
        if (!root) { return; }

        const t = e.target as HTMLElement | null;
        if (t && !root.contains(t)) {
            const cycle = getCycle(root);
            if (cycle.length > 0) {
                focusSafely(cycle[0]);
            } else if (includeRoot) {
                if (!root.hasAttribute('tabindex')) {
                    root.setAttribute('tabindex', '0');
                }
                focusSafely(root);
            }
        }
    }

    function enable() {
        if (enabled) { return; }

        const root = rootRef.value;
        if (!root) { return; }

        enabled = true;
        prevActive = (document.activeElement as HTMLElement) ?? null;

        if (includeRoot && !root.hasAttribute('tabindex')) {
            root.setAttribute('tabindex', '0');
        }

        const focusables = getFocusable(root);
        const init =
          resolveInitial(root) ||
          (includeRoot ? root : null) ||
          focusables[0] ||
          root;

        focusSafely(init);

        document.addEventListener('keydown', onKeyDown, true);
        document.addEventListener('focusin', onFocusIn, true);
    }

    function disable() {
        if (!enabled) { return; }

        enabled = false;

        document.removeEventListener('keydown', onKeyDown, true);
        document.removeEventListener('focusin', onFocusIn, true);

        if (returnFocus && prevActive) {
            focusSafely(prevActive);
            prevActive = null;
        }
    }

    onUnmounted(() => disable());

    return {
        enable,
        disable,
        isEnabled: () => enabled,
    };
}